import { useQuote } from "../QuoteProvider";
// ONE definition of "a valid US phone number" in the client. This module is
// pure (no React, no DOM, no imports of its own) and already backs the /live
// join + notify forms; duplicating its 10-or-11-digit NANP rule here is exactly
// the drift its own header warns about, so the funnel imports it instead.
import { isLooseUsPhone } from "../../livestream/liveContactValidation";

type User = {
  name?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  age?: number;
  weight?: number;
  heightFt?: number;
  heightIn?: number;
  zipCode?: string;
  hipaaAgreement?: boolean;
};

/**
 * Stable MACHINE keys for the contact step's missing-field set — not display
 * copy. StepContact maps them to localized patient-facing labels, and the
 * disabled-CTA tracker sends them verbatim (field names only, never values, so
 * the payload stays PII-free).
 *
 * Order is the on-screen order of the contact form, so "the first missing
 * field" is also the topmost one.
 */
export type MissingContactField =
  | "name"
  | "lastName"
  | "email"
  | "phone"
  | "phoneFormat"
  | "zipCode"
  | "hipaaAgreement";

/**
 * Missing-field key → the `quote` locale key of its patient-facing label.
 * Lives here so StepContact's inline hint and QuoteApp's `next()` backstop toast
 * name the very same things.
 */
export const CONTACT_FIELD_LABEL_KEYS: Record<MissingContactField, string> = {
  name: "contact.fieldName",
  lastName: "contact.fieldLastName",
  email: "contact.fieldEmail",
  phone: "contact.fieldPhone",
  phoneFormat: "contact.fieldPhoneInvalid",
  zipCode: "contact.fieldZipCode",
  hipaaAgreement: "contact.fieldTerms",
};

export const useQuoteFormValidation = () => {
  const {
    name,
    lastName,
    phone,
    email,
    age,
    weight,
    heightFt,
    heightIn,
    zipCode,
    hipaaAgreement,
  } = useQuote();

  const user: User = {
    name,
    lastName,
    phone,
    email,
    weight,
    heightFt,
    heightIn,
    age,
    zipCode,
    hipaaAgreement,
  };

  const missingUserFields = validateUser(user);
  const missingUserContactFields = missingContactFields(user);

  return {
    missingUserFields,
    missingUserContactFields,
  };
};

/**
 * What the contact step's CTA is gated on. Must stay in agreement with
 * QuoteApp's `next()` backstop for the Contact step — a field that blocks there
 * but not here renders the button enabled and then bounces the tap.
 *
 * `lastName` was missing here until 2026-09-13 even though `next()` has always
 * blocked on it (that was the bounce).
 */
export function missingContactFields(user: User): MissingContactField[] {
  const missingFields: MissingContactField[] = [];

  if (!user.name) missingFields.push("name");
  if (!user.lastName) missingFields.push("lastName");
  if (!user.email) missingFields.push("email");
  if (!user.phone) {
    missingFields.push("phone");
  } else if (!isLooseUsPhone(user.phone)) {
    // Present but not a usable US number. Kept as its own key so the hint can
    // say "check the format" rather than "you left it blank".
    missingFields.push("phoneFormat");
  }
  if (!user.zipCode) missingFields.push("zipCode");
  if (user.hipaaAgreement !== true) missingFields.push("hipaaAgreement");

  return missingFields;
}

function validateUser(user: User): string[] {
  const requiredFields: { [key in keyof User]: string } = {
    name: "Name",
    phone: "Phone",
    email: "Email",
    weight: "Weight",
    heightFt: "Height in feet",
    heightIn: "Height in inches",
    age: "Age",
  };

  const missingFields: string[] = [];

  for (const field in requiredFields) {
    if (user[field as keyof User] === "") {
      missingFields.push(requiredFields[field as keyof User]!);
    }
  }

  return missingFields;
}
